import { CoreStringValueBuilderPart, StringValueBuilderPartValueSource } from '../../../../../models/CoreModels';

export type StringValueBuilderPartField = keyof CoreStringValueBuilderPart;

export type FieldValidation = { field: StringValueBuilderPartField; message: string };

export type StringValueBuilderPartDefinition = {
        description: string;
        fields?: StringValueBuilderPartField[];
        requiredFields?: StringValueBuilderPartField[];
        validate: (part: CoreStringValueBuilderPart) => FieldValidation[];
        render: (part: CoreStringValueBuilderPart) => string;
        example: (part: CoreStringValueBuilderPart) => string;
};

export type StringValueBuilderPartDefinitions = {
    [key: number]: StringValueBuilderPartDefinition;
};

export const pickStringValueBuilderDefinitions = (
    source: StringValueBuilderPartDefinitions,
    allowed: StringValueBuilderPartValueSource[]
): StringValueBuilderPartDefinitions =>
    allowed.reduce((acc, key) => {
        const definition = source[key];
        if (definition) {
            acc[key] = definition;
        }
        return acc;
    }, {} as StringValueBuilderPartDefinitions);

export const defaultStringValueBuilderPartDefinitions: StringValueBuilderPartDefinitions = {
    [StringValueBuilderPartValueSource.PERSON_FIRST_NAME]: {
        description: 'stringValueBuilder.partDefinitions.personFirstName.description',
        fields: ["maxLength"],
        validate: (part) => {
            const validation: FieldValidation[] = [];
            if (part.maxLength !== undefined && part.maxLength < 1) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.personFirstName.validationMessage' });
            }

            return validation;
        },
        render: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return `stringValueBuilder.partDefinitions.personFirstName.firstNameRender`;
            } else {
                return `stringValueBuilder.partDefinitions.personFirstName.description`;
            }
        },
        example: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return "kalle".substring(0, part.maxLength);
            } else {
                return "kalle";
            }
        }
    },
    [StringValueBuilderPartValueSource.PERSON_LAST_NAME]: {
        description: 'stringValueBuilder.partDefinitions.personLastName.description',
        fields: ["maxLength"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            if (part.maxLength !== undefined && part.maxLength < 1) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.personLastName.validationMessage' });
            }

            return validation;
        },
        render: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return 'stringValueBuilder.partDefinitions.personLastName.lastNameRender';
            } else {
                return `stringValueBuilder.partDefinitions.personLastName.description`;
            }
        },
        example: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return "kula".substring(0, part.maxLength);
            } else {
                return "kula";
            }
        }
    },
    [StringValueBuilderPartValueSource.RANDOM_FROM_CHARSET]: {
        description: 'stringValueBuilder.partDefinitions.randomFromCharset.description',
        fields: ["minLength", "maxLength", "valueText"],
        requiredFields: ["minLength", "maxLength", "valueText"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            if (part.valueText === undefined || part.valueText.trim() === "") {
                validation.push({ field: "valueText", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageValueTexthUndefined' });
            }

            if (part.minLength === undefined) {
                validation.push({ field: "minLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMinLengthUndefined' });
            } else if (part.minLength < 1) {
                validation.push({ field: "minLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMinLength' });
            }

            if (part.maxLength === undefined) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMaxLengthUndefined' });
            }
            else if (part.maxLength > 2000) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMaxLength' });
            }
            else if (part.maxLength < (part.minLength ?? 0)) {
                validation.push({ field: "minLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMaxLowerMin' });
            }

            return validation;
        },
        render: (part) => {
            if (part.minLength === part.maxLength) {
                return 'stringValueBuilder.partDefinitions.randomFromCharset.render1';
            } else {
                return `stringValueBuilder.partDefinitions.randomFromCharset.render2`;
            }
        },
        example: (part) => {
            if (!part.maxLength || !part.valueText || !part.minLength) {
                return "";
            }

            let length = part.minLength + Math.floor((Math.random() * (part.maxLength - part.minLength + 1)));

            var str = "";
            for (let i = 0; i < length; i++) {
                str += part.valueText[Math.floor(Math.random() * part.valueText.length)]
            }

            return str;
        }
    },
    [StringValueBuilderPartValueSource.STUDENT_SCHOOL_YEAR]: {
        description: 'stringValueBuilder.partDefinitions.studentSchoolYear.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.studentSchoolYear.description',
        example: (_) => '3'
    },
    [StringValueBuilderPartValueSource.PERIOD_AWARE_START_YEAR]: {
        description: 'stringValueBuilder.partDefinitions.periodAwareStartYear.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.periodAwareStartYear.schoolYearStart',
        example: (_) => {
            const date = new Date();
            if (date.getMonth() < 6) {
                return (date.getFullYear() - 1).toString()
            } else {
                return date.getFullYear().toString()
            }
        }
    },
    [StringValueBuilderPartValueSource.PERIOD_AWARE_END_YEAR]: {
        description: 'stringValueBuilder.partDefinitions.periodAwareEndYear.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.periodAwareEndYear.schoolYearEnd',
        example: (_) => {
            const date = new Date();
            if (date.getMonth() < 6) {
                return date.getFullYear().toString()
            } else {
                return (date.getFullYear() + 1).toString()
            }
        }
    },
    [StringValueBuilderPartValueSource.CONSTANT]: {
        description: 'stringValueBuilder.partDefinitions.constant.description',
        fields: ["valueText"],
        requiredFields: ["valueText"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            // NOTE: Do not trim and check for whitespace since there is a possibility of wanting to use white space characters.
            if (part.valueText === undefined || part.valueText === "") {
                validation.push({ field: "valueText", message: 'stringValueBuilder.partDefinitions.constant.validationMessage' });
            }

            return validation;
        },
        render: (part) => `"${part.valueText}"`,
        example: (part) => part.valueText || ""
    },
    [StringValueBuilderPartValueSource.YEAR_OF_BIRTH]: {
        description: 'stringValueBuilder.partDefinitions.yearOfBirth.description',
        fields: ["maxLength"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            if ((part.maxLength !== undefined && part.maxLength !== null) && (part.maxLength < 1 || part.maxLength > 4)) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.yearOfBirth.validationMessageMaxLength' });
            }

            return validation;
        },
        render: (part) => {
            if (part.maxLength === undefined || part.maxLength === null) {
                return 'stringValueBuilder.partDefinitions.yearOfBirth.renderDefaultMaxLength';
            }
            else {
                return 'stringValueBuilder.partDefinitions.yearOfBirth.render';
            }
        },
        example: (part) => {
            const date = new Date();
            var ex = date.getFullYear().toString();

            if (!part.maxLength)
                return ex + ' ';

            let length = ex.length;
            return ex.substring(length - part.maxLength) + ' ';
        },
    },
    [StringValueBuilderPartValueSource.CLASS]: {
        description: 'stringValueBuilder.partDefinitions.class.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.class.render',
        example: (_) => {
            return "5A";
        },
    },
    [StringValueBuilderPartValueSource.STAGE]: {
        description: 'stringValueBuilder.partDefinitions.stage.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.stage.render',
        example: (_) => {
            return "F-3";
        },
    },
    [StringValueBuilderPartValueSource.PRIMARYSCHOOL]: {
        description: 'stringValueBuilder.partDefinitions.primarySchool.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.primarySchool.render',
        example: (_) => {
            return "Eventfulskolan";
        },
    },
    [StringValueBuilderPartValueSource.FORMOFEDUCATION]: {
        description: 'stringValueBuilder.partDefinitions.formOfEducation.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.formOfEducation.render',
        example: (_) => {
            return "Grundskola";
        },
    },
    [StringValueBuilderPartValueSource.ACTIVITYNAME]: {
        description: 'stringValueBuilder.partDefinitions.activityName.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.activityName.render',
        example: (_) => {
            return "7A Ma"; 
        },
    },
    [StringValueBuilderPartValueSource.GROUPNAME]: {
        description: 'stringValueBuilder.partDefinitions.groupName.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.groupName.render',
        example: (_) => {
            return "7";
        },
    },
    [StringValueBuilderPartValueSource.SUBJECTCODE]: {
        description: 'stringValueBuilder.partDefinitions.subjectCode.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.subjectCode.render',
        example: (_) => {
            return "Ma";
        },
    },
    [StringValueBuilderPartValueSource.SCHOOLNAME]: {
        description: 'stringValueBuilder.partDefinitions.schoolName.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.schoolName.render',
        example: (_) => {
            return "Eventfulskolan"; 
        },
    },
    [StringValueBuilderPartValueSource.SCHOOLNAME_SHORT]: {
        description: 'stringValueBuilder.partDefinitions.schoolNameShort.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.schoolNameShort.render',
        example: (_) => {
            return "EVT";
        },
    },
    [StringValueBuilderPartValueSource.SCHOOLNAME_SHORT_FALLBACK_SCHOOLNAME]: {
        description: 'stringValueBuilder.partDefinitions.schoolNameShortFallbackSchoolName.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.schoolNameShortFallbackSchoolName.render',
        example: (_) => {
            return "EVT (Eventfulskolan)";
        },
    },
};